/**
 * Daily's call object loads Krisp by evaluating
 * call-machine-object-bundle.js on every join. The second evaluation logs
 * "KrispSDK - The KrispSDK is duplicated".
 * Keep the first evaluation, and for later joins only register the new call.
 */

const CALL_MACHINE_BUNDLE = /\/call-machine\/(?:versioned\/[^/]+\/)?static\/call-machine-object-bundle\.js(?:[?#]|$)/;
const SETUP_CALL = /\(0,(\w+)\.setupCallMachine\)\((window\._daily\.pendings\[0\])\)/;

let missingSetupWarned = false;

function isCallMachineSource(source) {
  return typeof source === 'string'
    && source.includes('krisp-debug-widget')
    && source.includes('setupCallMachine')
    && source.includes('window._daily.pendings');
}

function injectSetupCallMachineExport(source) {
  if (!isCallMachineSource(source) || source.includes('__baglessSetupCallMachine')) return source;
  if (!SETUP_CALL.test(source)) {
    if (!missingSetupWarned) {
      missingSetupWarned = true;
      console.warn('[Bagless] Daily call machine has no setupCallMachine hook; Krisp may load again on the next call.');
    }
    return source;
  }
  return source.replace(
    SETUP_CALL,
    '(window.__baglessSetupCallMachine=$1.setupCallMachine,(0,$1.setupCallMachine))($2)',
  );
}

function installCallMachineFunctionGuard(root) {
  const NativeFunction = root.Function;
  function registerPendingCallMachine() {
    const daily = root._daily;
    const pending = daily && daily.pendings;
    if (root._dailyCallMachineLoadWaitlist && root._dailyCallMachineLoadWaitlist.clear) {
      root._dailyCallMachineLoadWaitlist.clear();
    }
    if (pending && pending.length && typeof root.__baglessSetupCallMachine === 'function') {
      root.__baglessSetupCallMachine(pending[0]);
      pending.shift();
    }
  }
  if (!NativeFunction || NativeFunction.__baglessKrispGuard) return;
  const WrappedFunction = function Function(...args) {
    const body = args[args.length - 1];
    if (isCallMachineSource(body)) {
      if (typeof root.__baglessSetupCallMachine === 'function') {
        return registerPendingCallMachine;
      }
      const patched = injectSetupCallMachineExport(body);
      const nextArgs = patched === body ? args : args.slice(0, -1).concat(patched);
      return new.target
        ? Reflect.construct(NativeFunction, nextArgs, NativeFunction)
        : NativeFunction.apply(this, nextArgs);
    }
    return new.target
      ? Reflect.construct(NativeFunction, args, new.target)
      : NativeFunction.apply(this, args);
  }
  Object.setPrototypeOf(WrappedFunction, NativeFunction);
  WrappedFunction.prototype = NativeFunction.prototype;
  WrappedFunction.__baglessKrispGuard = true;
  try {
    Object.defineProperty(NativeFunction.prototype, 'constructor', {
      value: WrappedFunction,
      writable: true,
      configurable: true,
    });
  } catch (e) {
    // Leave the original constructor if the runtime forbids the change.
  }
  root.Function = WrappedFunction;
}

function callMachineStub() {
  return '!function(){var d=window._daily,p=d&&d.pendings;if(window._dailyCallMachineLoadWaitlist&&window._dailyCallMachineLoadWaitlist.clear)window._dailyCallMachineLoadWaitlist.clear();if(p&&p.length&&typeof window.__baglessSetupCallMachine==="function"){window.__baglessSetupCallMachine(p[0]);p.shift();}}();';
}

function requestUrl(input) {
  if (typeof input === 'string') return input;
  if (input && typeof input.url === 'string') return input.url;
  return '';
}

function installSingleKrispSdk(target) {
  const root = target || (typeof globalThis !== 'undefined' ? globalThis : null);
  if (!root || root.__baglessSingleKrispSdk || typeof root.fetch !== 'function') return;
  root.__baglessSingleKrispSdk = true;
  installCallMachineFunctionGuard(root);
  const nativeFetch = root.fetch.bind(root);

  root.fetch = function baglessFetch(input, init) {
    const url = requestUrl(input);
    if (!CALL_MACHINE_BUNDLE.test(url)) {
      return nativeFetch(input, init);
    }
    if (typeof root.__baglessSetupCallMachine === 'function') {
      return Promise.resolve(new Response(callMachineStub(), {
        status: 200,
        headers: { 'Content-Type': 'application/javascript' },
      }));
    }
    return nativeFetch(input, init).then(async (response) => {
      if (!response || !response.ok || typeof response.text !== 'function') return response;
      const source = await response.text();
      const contentType = response.headers && typeof response.headers.get === 'function'
        ? (response.headers.get('content-type') || 'application/javascript')
        : 'application/javascript';
      return new Response(injectSetupCallMachineExport(source), {
        status: response.status,
        statusText: response.statusText,
        headers: { 'Content-Type': contentType },
      });
    });
  };
}

installSingleKrispSdk();
